<template>
  <div class="right-panel">
    <div id="right-panel-container"></div>
    <div class="clear-filter-container">
      <button
        class="clear-filter p-1 rounded-md border-2 m-2"
        @click="$emit('cleanSelection')"
      >
        Limpiar filtros
      </button>
      <button class="clear-filter p-1 rounded-md border-2 m-2" onclick="location.href='https://storage.googleapis.com/cartografiadigital/base_de_datos.xlsx';">
        Descargar base de datos
      </button>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'RightPanel',
  props: [
    'records',
    'selection',
  ],
  data(){
    return {
      container: null,
      sliderStyle: {
        backgroundColor: "var(--black)",
      },
      maxR: 20,
      pieR: 30,
      titleFontSize: 12,
      categoryFontSize: 8,
      techniqueHorizontalOffset: 0,
      formatHorizontalOffset: 200,
      hoveredTechnique: null,
      // techniqueHorizontalOffset: 90,
    }
  },
  mounted() {
    this.processData();
    this.createMap();
    this.draw();
  },
  methods: {
    processData() {

      // CC processing
      this.cc = new Map();
      this.records.forEach(record => {
        const cc = record.metadata.cc;
        if (!this.cc.has(cc)) this.cc.set(cc, 1);
        else {
          this.cc.set(cc, this.cc.get(cc) + 1);
        }   
      });
      this.ccDatum = {};
      [...this.cc].forEach(([name, count]) => {
        this.ccDatum[name] = count;
      });

      // Technique processing
      this.techniques = new Map();
      this.records.forEach(record => {
        record.metadata.techniques.forEach(technique => {
          if (!this.techniques.has(technique)) this.techniques.set(technique, 1);
          else {
            this.techniques.set(technique, this.techniques.get(technique) + 1);
          }
        })
      });
      this.techniqueDatum = {};
      [...this.techniques].forEach(([name, count]) => {
        this.techniqueDatum[name] = count;
      });

      // Format processing
      this.formats = new Map();
      this.records.forEach(record => {
        record.metadata.format.forEach(technique => {
          if (technique.length) {
            if (!this.formats.has(technique)) this.formats.set(technique, 1);
            else {
              this.formats.set(technique, this.formats.get(technique) + 1);
            }
          }
        })
      });
      this.formatDatum = {};
      [...this.formats].forEach(([name, count]) => {
        this.formatDatum[name] = count;
      });

      // Create scales
      this.createScales();  
    },

    // The next 3 functions set up the scales of the idiom

    // Techinque Scales
    createTechniqueScales() {
      this.techniqueData = d3.entries(this.techniqueDatum);
      const maxVal = d3.max(this.techniqueData, d => d.value);
      const categories = this.techniqueData.map(d => d.key);

      this.techniqueScales = {
        r: d3.scaleSqrt().domain([0, maxVal]).range([0, this.maxR]),
        y: d3.scaleOrdinal().domain(categories.length).range(categories.map((d, idx) => idx * this.maxR))
      };
    },

    // Format Scales
    createFormatScales() {
      this.formatData = d3.entries(this.formatDatum);
      const maxVal = d3.max(this.formatData, d => d.value);
      const categories = this.formatData.map(d => d.key);

      this.formatScales = {
        r: d3.scaleSqrt().domain([0, maxVal]).range([0, this.maxR]),
        y: d3.scaleOrdinal().domain(categories.length).range(categories.map((d, idx) => idx * this.maxR))
      };
    },

    // Call all scales
    createScales(){
      this.createTechniqueScales();
      this.createFormatScales();
    },

    createMap() {
      this.container = d3.select('#right-panel-container')
        .append('svg')
        .attr('viewBox', '0 0 200 420')
        .append('g')
        .attr('transform', 'translate(' + 50 + ',' + 40  + ')');
    },

    // Next 3 functions draw the idioms

    // Draws Techinques
    drawTechniques() {
      // Title
      this.container.append('text')
        .text('Técnica')
        .attr("x", -50)
        .attr('y', this.techniqueHorizontalOffset - 25) 
        .attr("font-size", `${this.titleFontSize}px`)
        .attr('class', 'italic');
      // Circle
      this.container.selectAll('.technique-circle')
        .data(this.techniqueData)
        .enter()
        .append('circle')
        .attr('class', 'technique-circle cursor-pointer')
        .style('mix-blend-mode', 'multiply')
        .attr('fill', '#dfdfdf')
        .attr('stroke', 'none')
        .attr('cx', -20)
        .attr('cy', d => this.techniqueScales.y(d.key))
        .attr('r', d => this.techniqueScales.r(d.value))
        .attr('transform', `translate(0, ${this.techniqueHorizontalOffset})`)
        .on('mouseover', this.hoverTechnique)
        .on('mouseout', this.unhoverTechnique)
        .on('click', this.selectTechnique); 
      // Dot 
      this.container.selectAll('.technique-dot')
        .data(this.techniqueData)
        .enter()
        .append('circle')
        .attr('fill', 'black')
        .attr('stroke', 'none')
        .attr('cx', -20)
        .attr('cy', d => this.techniqueScales.y(d.key))
        .attr('r', 1)
        .attr('transform', `translate(0, ${this.techniqueHorizontalOffset})`);  
      // Name
      this.container.selectAll('technique-text')
        .data(this.techniqueData)
        .enter()
        .append('text')
        .attr('class', 'technique-text cursor-pointer')
        .attr('x', this.maxR / 2 - 20)
        .attr('y', d => this.techniqueScales.y(d.key))
        .text(d => d.key)
        .attr("font-size", `${this.categoryFontSize}px`)
        .style('dominant-baseline', 'central')
        .attr('transform', `translate(0, ${this.techniqueHorizontalOffset})`)
        // .on('mouseover', this.hoverTechnique)
        // .on('mouseout', this.unhoverTechnique)
        .on('click', this.selectTechnique);
    },

    // Draws Formats
    drawFormats() {
      // Title
      this.container.append('text')
        .text('Formatos')
        .attr("x", -50)
        .attr('y', this.formatHorizontalOffset - 25) 
        .attr("font-size", `${this.titleFontSize}px`)
        .attr('class', 'italic');
      // Circle
      this.container.selectAll('.format-circle')
        .data(this.formatData)
        .enter()
        .append('circle')
        .attr('class', 'format-circle cursor-pointer')
        .style('mix-blend-mode', 'multiply')
        .attr('fill', '#dfdfdf')
        .attr('stroke', 'none')
        .attr('cx', -20)
        .attr('cy', d => this.formatScales.y(d.key))
        .attr('r', d => this.formatScales.r(d.value))
        .attr('transform', `translate(0, ${this.formatHorizontalOffset})`)
        .on('mouseover', this.hoverFormat)
        .on('mouseout', this.unhoverFormat)
        .on('click', this.selectFormat);  
      // Dots
      this.container.selectAll('.format-dot')
        .data(this.formatData)
        .enter()
        .append('circle')
        .attr('fill', 'black')
        .attr('stroke', 'none')
        .attr('cx', -20)
        .attr('cy', d => this.formatScales.y(d.key))
        .attr('r', 1)
        .attr('transform', `translate(0, ${this.formatHorizontalOffset})`);  
      // Name
      this.container.selectAll('format-text')
        .data(this.formatData)
        .enter()
        .append('text')
        .attr('class', 'format-text cursor-pointer')
        .attr('x', this.maxR / 2 - 20)
        .attr('y', d => this.formatScales.y(d.key))
        .text(d => d.key)
        .attr("font-size", `${this.categoryFontSize}px`)
        .style('dominant-baseline', 'central')
        .attr('transform', `translate(0, ${this.formatHorizontalOffset})`)
        .on('mouseover', this.hoverFormat)
        .on('mouseout', this.unhoverFormat)
        .on('click', this.selectFormat);
    },

    // Calls drawing functions
    draw() {
      this.drawTechniques();
      this.drawFormats();
    },

    hoverTechnique(d) {
      // this.$emit('hover', 'technique', d.key);
      this.container.selectAll('.technique-circle')
        .transition()
        .duration(200)
        .attr('opacity', n => n.key === d.key ? 1 : 0.2);
      this.container.selectAll('.technique-text')
        .transition()
        .duration(200)
        .attr('opacity', n => n.key === d.key ? 1 : 0.2);
    },

    unhoverTechnique() {
      this.$emit('unhover');
      const { technique } = this.selection;
      this.container.selectAll('.technique-circle')
        .transition()
        .duration(200)
        .attr('opacity', d => !technique ? 1 : d.key === technique ? 1 : 0.2);
      this.container.selectAll('.technique-text')
        .transition()
        .duration(200)
        .attr('opacity', d => !technique ? 1 : d.key === technique ? 1 : 0.2);
    },

    hoverFormat(d) {
      this.$emit('hover', 'format', d.key);
      this.container.selectAll('.format-circle')
        .transition()
        .duration(200)
        .attr('opacity', n => n.key === d.key ? 1 : 0.2);
      this.container.selectAll('.format-text')
        .transition()
        .duration(200)
        .attr('opacity', n => n.key === d.key ? 1 : 0.2);
    },

    unhoverFormat() {
      this.$emit('unhover');
      const { format } = this.selection;
      this.container.selectAll('.format-circle')
        .transition()
        .duration(200)
        .attr('opacity', d => !format ? 1 : d.key === format ? 1 : 0.2);
      this.container.selectAll('.format-text')
        .transition()
        .duration(200)
        .attr('opacity', d => !format ? 1 : d.key === format ? 1 : 0.2);
    },

    selectTechnique(d) {
      this.$emit('select', 'technique', d.key);
    },

    selectFormat(d) {
      this.$emit('select', 'format', d.key);
    },
  },
  watch: {
    selection: {
      handler: function(){
        // Technique watcher
        const { technique } = this.selection;
        if (technique) {
          this.container.selectAll('.technique-circle')
            .transition()
            .duration(200)
            .attr('opacity', d => d.key === technique ? 1 : 0.2);
          this.container.selectAll('.technique-text')
            .transition()
            .duration(200)
            .attr('opacity', d => d.key === technique ? 1 : 0.2);
            }
        else {
          this.container.selectAll('.technique-circle')
            .transition()
            .duration(200)
            .attr('opacity', 1);
          this.container.selectAll('.technique-text')
            .transition()
            .duration(200)
            .attr('opacity', 1);
        }

        // Format watcher
        const { format } = this.selection;
        if (format) {
          this.container.selectAll('.format-circle')
            .transition()
            .duration(200)
            .attr('opacity', d => d.key === format ? 1 : 0.2);
          this.container.selectAll('.format-text')
            .transition()
            .duration(200)
            .attr('opacity', d => d.key === format ? 1 : 0.2);
        }
        else {
          this.container.selectAll('.format-circle')
            .transition()
            .duration(200)
            .attr('opacity', 1);
          this.container.selectAll('.format-text')
            .transition()
            .duration(200)
            .attr('opacity', 1);
        }
      },
      deep: true,
    },
  },
}
</script>

<style>

  .right-panel {
    width: 100%;
  }

  #right-panel-container {
    width: 100%;
    font-family: var(--courier);
  }

  .blended {
    font-style: italic;
  }

  .clear-filter-container {
    display: flex;
  }

  .clear-filter {
    font-family: var(--courier);
    border-color: var(--black);;
  }

</style>